
import { computed, onMounted, defineComponent, reactive, onBeforeMount, watch } from 'vue';
import {
  CustomerStampCardParsed,
  ShareCampaignGoal,
  StampCardParsed,
  StampCardSettings,
  TERM_OPTION,
  TERM_DAYS_LIMIT_OPTION,
} from '@comsbi/common/src/types/models';
import { datetimeFormat } from '@/utils';
import dayjs from 'dayjs';
import { useStampCard } from '@/compositions/useStampCard';
import QrCodeLegalNotice from '@/components/atoms/QrCodeLegalNotice.vue';

export default defineComponent<{ card?: CustomerStampCardParsed; master?: StampCardParsed; gotStamp: boolean }>({
  name: 'StampCardPageContents',

  components: { QrCodeLegalNotice },

  props: {
    card: {
      type: Object,
      default: () => null,
    },
    master: {
      type: Object,
      default: () => null,
    },
    gotStamp: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    const { getFirstStampTimestamp, getLatestTimestamp, isPastDays, resetCustomerStamps } = useStampCard();

    const state = reactive({
      // 日数指定用モーダル
      termDaysDialog: false,
      hasTermDaysExpired: false,
      // モーダル文言切り替え用
      loading: false,
    });

    const settings = computed<StampCardSettings | undefined>(() => props.master?.settings);

    const stamps = computed(() => props.card?.contents.stamps || []);
    const histories = computed(() => props.card?.contents.histories || []);
    const deletedStamps = computed(() => props.card?.contents.deletedStamps || []);
    const id = computed(() => props.card?.stampCardId || '');
    // そのラウンドの最大スタンプ数
    const maxOfStamps = computed(() => {
      return props.master ? Math.max(...props.master?.settings.goals.map(v => v.numOfStamps)) : 0;
    });

    const currentRound = computed(() => {
      // FIXME: ラウンドの最後に到達した時の周数表示
      // historiesへの保存処理タイミングを調整する必要あり
      if (stamps.value.length === maxOfStamps.value) {
        return histories.value.length;
      } else {
        return histories.value.length + 1;
      }
    });

    // CS-7533 n周対応
    const repeatRoundEnabled = computed(() => settings.value?.repeatRoundEnabled ?? false);

    const findStamp = (key: string) => props.card?.contents.stamps.find(v => v.itemKey === key);

    // postbackが仕込まれているか判定
    const findGoal = (stampNumber: number) => {
      const goals = props.master?.settings.goals || [];

      if (repeatRoundEnabled.value) {
        // 周回指定していないデフォルトのゴール
        const defaultGoal = goals.find(v => {
          // NOTE: isGoalは追加されたプロパティなので、存在しない場合は次の処理へ
          if (v?.isGoal === false) return false;
          const hasDefaultGoal = !v.round && v.round !== 0;
          return hasDefaultGoal && v.numOfStamps === stampNumber;
        });

        // 周回指定しているゴール
        const roundGoal = goals.find(v => {
          // NOTE: isGoalは追加されたプロパティなので、存在しない場合は次の処理へ
          if (v?.isGoal === false) return false;
          return v.round === currentRound.value - 1 && v.numOfStamps === stampNumber;
        });
        return roundGoal || defaultGoal;
      } else {
        return goals.find(v => {
          // NOTE: isGoalは追加されたプロパティなので、存在しない場合は次の処理へ
          if (v?.isGoal === false) return false;
          return v.numOfStamps === stampNumber;
        });
      }
    };

    const nextGoal = computed(() => {
      const goals = (props.master?.settings.goals || [])
        .sort((a: ShareCampaignGoal, b: ShareCampaignGoal) => a.numOfStamps - b.numOfStamps)
        .filter((v: ShareCampaignGoal) => v.numOfStamps > stamps.value.length);
      if (repeatRoundEnabled.value) {
        return goals.find(v => !v.round || v.round === histories.value.length);
      } else {
        return goals.length > 0 ? goals[0] : null;
      }
    });

    const completed = computed(() => {
      // return !!props.card?.completedAt; // CS-7638 初回コンプリート日時は運用次第で無効化される可能性がある
      return maxOfStamps.value <= stamps.value.length;
    });

    // 有効期間
    const termType = computed(() => settings.value?.termType || TERM_OPTION.UNLIMITED);
    const termDaysText = computed(() => {
      if (termType.value !== TERM_OPTION.DAYS || !stamps.value.length || !settings.value) return '';
      const targetTimeStamp =
        settings.value.termDaysLimitOption === TERM_DAYS_LIMIT_OPTION.FIRST
          ? getFirstStampTimestamp(stamps.value)
          : getLatestTimestamp(stamps.value);
      const termDays = settings.value?.termDays || 0;
      if (targetTimeStamp) {
        const expiredDate = dayjs(new Date(targetTimeStamp)).add(termDays, 'day');
        return datetimeFormat(expiredDate.toString(), 'YYYY年 M月D日 HH:mm');
      }
      return '';
    });

    const termEnabled = computed(() => {
      if (!settings.value?.term) return true;
      const [from, to] = settings.value.term;
      const now = dayjs();
      if (from && now.isBefore(from)) return false;
      if (to && now.isAfter(to)) return false;
      return true;
    });

    const reset = async () => {
      state.loading = true;
      const customerStampCardId = props.card?.id || '';
      const result = await resetCustomerStamps(customerStampCardId, false);
      if (result.status === 200) {
        emit('reload');
      }
      state.loading = true;
      state.termDaysDialog = false;
    };

    onMounted(async () => {
      // スタンプがない場合はリセット云々は関係ない
      if (!stamps.value.length || !settings.value) return;
      // リスト型 && 日数指定リセットが無効 の場合はリセットモーダルを表示しない
      if (!settings.value.resetOnExpiration) return;

      // 日数指定のみ リセット可能有効期限切れモーダル表示
      if (termType.value === TERM_OPTION.DAYS) {
        const targetTimeStamp =
          settings.value.termDaysLimitOption === TERM_DAYS_LIMIT_OPTION.FIRST
            ? getFirstStampTimestamp(stamps.value)
            : getLatestTimestamp(stamps.value);
        const isPast = targetTimeStamp ? isPastDays(targetTimeStamp, settings.value?.termDays || 0) : false;
        state.termDaysDialog = isPast;
      }
    });

    onBeforeMount(async () => {
      // CS-8304 既存データとの整合性用処理
      // stampsがmaxOfStampsを超えている場合は過去データとみなしリセットする
      if (stamps.value.length <= maxOfStamps.value) return;
      try {
        emit('loadingStart');
        const customerStampCardId = props.card?.id || '';
        const result = await resetCustomerStamps(customerStampCardId, true);
        if (result.status === 200) {
          emit('reload');
        }
      } catch (e) {
        console.log(e);
      } finally {
        emit('loadingEnd');
      }
    });

    return {
      state,
      settings,

      // 有効期間
      termType,
      TERM_OPTION,
      termDaysText,

      id,
      stamps,
      histories,
      deletedStamps,
      currentRound,
      reset,

      findGoal,
      findStamp,
      repeatRoundEnabled,
      maxOfStamps,
      nextGoal,
      termEnabled,

      completed,

      // debug
      datetimeFormat,
      isDebug: process.env.NODE_ENV === 'local' || process.env.NODE_ENV === 'development',
      historiesStampTotal: computed(() => {
        let totalStampCount = 0;
        histories.value.forEach(history => {
          totalStampCount += history.stamps.length;
        });
        return totalStampCount;
      }),
    };
  },
});
